/**
 * Marketing demo rooms — preseed the canvas via `?demo=<id>`.
 *
 * Cold ad traffic that lands on an empty canvas has nothing to look
 * at; the first-screen experience for a `?demo=living` link is a
 * fully-furnished room with the silhouette already proving scale. The
 * UTM flow is:
 *
 *   Meta Reel ad  -->  https://app/?demo=living&utm_source=meta&…
 *
 * Each Reel script in [marketing/reels-scripts.md](../../marketing/reels-scripts.md)
 * specifies the demo id it expects. Adding a new creative concept
 * means adding (or reusing) one of the entries below.
 *
 * Safety: {@link maybeSeedDemoRoom} ONLY replaces the active design
 * when the canvas is genuinely empty (no walls placed yet). Returning
 * users who happen to click a campaign link keep their work intact.
 */

import type { Design, Room } from "./types";
import { buildFengShuiStarterDesign } from "./fengShuiExample";
import { buildRender3dShowcaseDesign } from "./render3dShowcase";
import { presetShape } from "./geom";
import { useDesign, isUnstartedRoom } from "./store";
import { track, addBreadcrumb } from "./telemetry";

/** Same key `Onboarding.tsx` checks; set here so a seeded room is never
 *  covered by the "sketch your room" welcome modal. */
export const ONBOARDING_SEEN_KEY = "room-canvas:onboarding:seen";

/** Stable ids the URL `?demo=` param accepts. Closed union so a
 *  rogue link can't seed an unknown design. */
export type DemoRoomId = "living" | "bedroom";

const FT = 12;

/**
 * 16'x12' living room. The shot list for Concept A ("The Sofa Return")
 * and Concept B ("Finger-sketch in 30 seconds") both open against this
 * layout, so the first frame of either ad and the first paint on the
 * destination match visually. Furniture names are exact catalog
 * preset names so the inspector and AI integration work normally.
 */
function livingRoom(): Design {
  const points = presetShape("rectangle", 16 * FT, 12 * FT);
  // Helper: at rotation: 0, +y is the front. See `FrontKind` in
  // [src/lib/catalog.ts]. Coordinates are in inches with origin at
  // top-left; y grows downward.
  return {
    room: {
      id: "demo_room_living",
      name: "Demo living room",
      points,
      ceilingHeight: 8 * FT,
      wallThickness: 5,
      shapeKind: "rectangle",
    },
    furniture: [
      {
        id: "demo_f_rug",
        name: "Area rug 5x7",
        category: "decor",
        width: 60,
        depth: 84,
        height: 1,
        x: 96,
        y: 78,
        rotation: 0,
        color: "#c39a72",
        icon: "rug",
      },
      {
        id: "demo_f_sofa",
        name: "3-seat sofa",
        category: "seating",
        width: 84,
        depth: 36,
        height: 34,
        x: 96,
        y: 24,
        rotation: 0,
        color: "#4f6c8f",
        icon: "sofa",
      },
      {
        id: "demo_f_coffee",
        name: "Coffee table",
        category: "tables",
        width: 48,
        depth: 24,
        height: 18,
        x: 96,
        y: 68,
        rotation: 0,
        color: "#8a6a47",
        icon: "table",
      },
      {
        id: "demo_f_armchair",
        name: "Armchair",
        category: "seating",
        width: 34,
        depth: 36,
        height: 34,
        x: 160,
        y: 108,
        rotation: 270,
        color: "#6b8aab",
        icon: "armchair",
      },
      {
        id: "demo_f_tv_stand",
        name: "TV stand",
        category: "storage",
        width: 60,
        depth: 18,
        height: 24,
        x: 96,
        y: 132,
        rotation: 180,
        color: "#3a3a44",
        icon: "tv-stand",
      },
      {
        id: "demo_f_plant",
        name: "Plant (large)",
        category: "decor",
        width: 24,
        depth: 24,
        height: 60,
        x: 24,
        y: 108,
        rotation: 0,
        color: "#5e8a5e",
        icon: "plant",
      },
    ],
    // Top-down view doesn't need wall mounts for the first frame. The
    // Concept A creative cuts to a wall-elevation view of a 65" TV
    // that the user (or the recorder) places mid-shot — leaving the
    // demo without one preserves the "drop the TV in for me" beat.
    mounts: [],
    openings: [],
    person: {
      heightInches: 71,
      weightLbs: 170,
      position: null,
    },
    unit: "imperial",
    consultations: [],
    rankings: [],
  };
}

/**
 * 12'x11' bedroom. Reserve seed for a future "Will the bed fit?"
 * creative — same value prop, swapped category.
 */
function bedroom(): Design {
  const points = presetShape("rectangle", 12 * FT, 11 * FT);
  return {
    room: {
      id: "demo_room_bedroom",
      name: "Demo bedroom",
      points,
      ceilingHeight: 8 * FT,
      wallThickness: 5,
      shapeKind: "rectangle",
    },
    furniture: [
      {
        id: "demo_f_bed",
        name: "Queen bed",
        category: "beds",
        width: 60,
        depth: 80,
        height: 25,
        x: 72,
        y: 44,
        rotation: 180,
        color: "#c2a17a",
        icon: "bed",
      },
      {
        id: "demo_f_ns_l",
        name: "Nightstand",
        category: "beds",
        width: 22,
        depth: 18,
        height: 26,
        x: 26,
        y: 14,
        rotation: 0,
        color: "#a48560",
        icon: "square",
      },
      {
        id: "demo_f_ns_r",
        name: "Nightstand",
        category: "beds",
        width: 22,
        depth: 18,
        height: 26,
        x: 118,
        y: 14,
        rotation: 0,
        color: "#a48560",
        icon: "square",
      },
      {
        id: "demo_f_dresser",
        name: "Dresser",
        category: "storage",
        width: 60,
        depth: 18,
        height: 32,
        x: 72,
        y: 119,
        rotation: 180,
        color: "#6e574a",
        icon: "drawer",
      },
    ],
    mounts: [],
    openings: [],
    person: {
      heightInches: 71,
      weightLbs: 170,
      position: null,
    },
    unit: "imperial",
    consultations: [],
    rankings: [],
  };
}

const DEMO_ROOMS: Record<DemoRoomId, () => Design> = {
  living: livingRoom,
  bedroom: bedroom,
};

function isDemoId(s: string): s is DemoRoomId {
  return s === "living" || s === "bedroom";
}

/**
 * If the URL carries a `?demo=<id>` param AND the active design is
 * still the empty boot state (no room walls placed), replace the
 * design with the matching seeded sample and switch into the Design
 * view.
 *
 * Critically guarded by {@link isUnstartedRoom} so returning users
 * who happen to land via a campaign link don't lose their in-flight
 * work. Suppresses the first-run Onboarding modal at the same time
 * so the seeded room is visible immediately — the modal teaches the
 * "sketch a room" concept which is the opposite of what the demo is
 * showing.
 *
 * Returns true iff a demo was seeded.
 */
export function maybeSeedDemoRoom(): boolean {
  if (typeof window === "undefined") return false;
  const sp = new URLSearchParams(window.location.search);
  const raw = sp.get("demo");
  if (!raw) return false;
  if (!isDemoId(raw)) {
    addBreadcrumb("demo:unknown-id", { id: raw });
    return false;
  }

  const state = useDesign.getState();
  if (!isUnstartedRoom(state.design.room)) {
    // The user has already started a real design — never clobber
    // their work. We still want analytics to see that the ad got
    // them here even on a return visit, so emit the event and bail.
    addBreadcrumb("demo:skipped:room-started");
    track("demo_seeded", { id: raw });
    return false;
  }

  state.loadDesign(DEMO_ROOMS[raw]());
  // Skip the welcome modal — its content ("sketch your room with
  // your finger") undermines the "look, it's already designed" wow
  // moment of the demo. Same key the modal checks.
  markOnboardingSeen();
  track("demo_seeded", { id: raw });
  addBreadcrumb("demo:seeded", { id: raw });
  return true;
}

function markOnboardingSeen(): void {
  try {
    window.localStorage.setItem(ONBOARDING_SEEN_KEY, "1");
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// Starter rooms — the collaboration first run
// ---------------------------------------------------------------------------

/**
 * Room ids of the furnished "starter" designs the collaboration first run
 * seeds. The prefix tells "the visitor is still in the room we gave them"
 * from "they built their own".
 */
export const STARTER_ROOM_ID_PREFIX = "starter_";

export function isStarterRoom(room: Pick<Room, "id">): boolean {
  return room.id.startsWith(STARTER_ROOM_ID_PREFIX);
}

/**
 * Message-match the starter room to the creative that brought the
 * visitor. Anything mentioning a bedroom gets the bedroom; every other
 * creative (including the collaboration Reel) opens on the living room,
 * which is the scene those ads are shot from.
 */
export function pickLandingRoom(utmContent: string | null): DemoRoomId {
  return /bed/i.test(utmContent ?? "") ? "bedroom" : "living";
}

/** The demo design re-labelled as the visitor's own starter room. */
export function buildStarterDesign(id: DemoRoomId): Design {
  const design = DEMO_ROOMS[id]();
  design.room = {
    ...design.room,
    id: `${STARTER_ROOM_ID_PREFIX}${id}`,
    name: id === "bedroom" ? "Our bedroom" : "Our living room",
  };
  return design;
}

/**
 * Replace the empty boot canvas with a furnished starter room and land
 * in the Design view. Guarded by {@link isUnstartedRoom} exactly like
 * {@link maybeSeedDemoRoom}; returns `null` when there was work to
 * protect. The caller (`CampaignLandingGate`) has already decided this
 * visitor should get the collaboration first run.
 */
export function seedLandingRoom(utmContent: string | null): {
  id: DemoRoomId;
  roomId: string;
} | null {
  const state = useDesign.getState();
  if (!isUnstartedRoom(state.design.room)) {
    addBreadcrumb("landing:skipped:room-started");
    return null;
  }
  const id = pickLandingRoom(utmContent);
  const design = buildStarterDesign(id);
  state.loadDesign(design);
  markOnboardingSeen();
  track("landing_room_seeded", { id, utm_content: utmContent });
  addBreadcrumb("landing:seeded", { id, utm_content: utmContent });
  return { id, roomId: design.room.id };
}

/**
 * Seed the feng shui first run's "coffin position" bedroom (the AI ad's
 * room, see `./fengShuiExample.ts`). Same guard and side effects as
 * {@link seedLandingRoom}; returns the room id, or `null` when there was
 * work to protect.
 */
export function seedFengShuiRoom(utmContent: string | null): string | null {
  const state = useDesign.getState();
  if (!isUnstartedRoom(state.design.room)) {
    addBreadcrumb("landing:skipped:room-started");
    return null;
  }
  const design = buildFengShuiStarterDesign();
  state.loadDesign(design);
  markOnboardingSeen();
  track("landing_room_seeded", { id: "feng_shui", utm_content: utmContent });
  addBreadcrumb("landing:seeded", { id: "feng_shui", utm_content: utmContent });
  return design.room.id;
}

/**
 * Seed the 3D first run's showcase living room (the 3D ad's room, see
 * `./render3dShowcase.ts`). Same guard and side effects as
 * {@link seedLandingRoom}; returns the room id, or `null` when there was
 * work to protect.
 */
export function seedRender3dRoom(utmContent: string | null): string | null {
  const state = useDesign.getState();
  if (!isUnstartedRoom(state.design.room)) {
    addBreadcrumb("landing:skipped:room-started");
    return null;
  }
  const design = buildRender3dShowcaseDesign();
  state.loadDesign(design);
  markOnboardingSeen();
  track("landing_room_seeded", { id: "render_3d", utm_content: utmContent });
  addBreadcrumb("landing:seeded", { id: "render_3d", utm_content: utmContent });
  return design.room.id;
}

/** Suppress the first-run welcome modal (the feng shui handoff lands on a blank room). */
export function suppressWelcomeModal(): void {
  markOnboardingSeen();
}
